'use client'

import { RentalCard } from '@/components/molecules/rental-card.client'
import { RentalEmptyState } from '@/components/molecules/rental-empty-state'
import { RentalListingTerms } from '@/components/molecules/rental-listing-terms'
import { RentalSearch } from '@/components/molecules/rental-search.client'
import { RentalToolbar } from '@/components/organisms/rental-toolbar.client'
import { useRentalListing } from '@/components/sections/use-rental-listing.client'
import { CONTAINER, SCREEN_TOP } from '@/lib/layout'
import type { RentalCategoryView, RentalItemView } from '@/lib/rental'
import { cn } from '@/lib/utils'

/**
 * Equipment rental listing.
 *
 * The catalogue arrives as props, read from the offering tables by the route's
 * server component, so what the panel edits is what this draws. Filtering,
 * sorting and pricing all happen in `useRentalListing`; this file is the
 * composition and nothing else.
 *
 * ## The redesign (owner-approved mock-up, October 2026)
 *
 * **The search is the first thing under the navigation, with nothing above
 * it.** The headline („Potrzebujesz na kilka dni. Nie na zawsze.") and the
 * fact chips went: on a page whose subject is the equipment, the tool beats
 * the slogan. The page keeps an `sr-only` h1 so its outline still has a title.
 *
 * **The category pills stay a separate row** (`RentalToolbar`), not inside
 * the search panel — the owner tried both and wanted them apart.
 *
 * **Four across, full width.** Every card opens the item's own page, so there
 * is no basket beside the grid; a fifth column on wide screens was dropped for
 * a calmer, larger card.
 *
 * **The foot is a bento of advantages** (`RentalListingTerms`) rather than the
 * three rules `RentalTerms` still prints on the item page — no deposit, the
 * cheapest day, a day that starts at pickup.
 *
 * **The empty state renders instead of the grid, not after it**, so the
 * accessibility tree never carries an empty `<ul>` beside it.
 */
export function RentalPageView({
  locale,
  categories,
  items,
}: {
  locale: string
  /** Filter pills, in catalogue order. */
  categories: RentalCategoryView[]
  /** Active equipment, in catalogue order. */
  items: RentalItemView[]
}) {
  const listing = useRentalListing(items)
  const rates = items.flatMap((item) => (item.dailyRateNet === null ? [] : [item.dailyRateNet]))
  const fromDailyNet = rates.length > 0 ? Math.min(...rates) : null

  return (
    <div className="relative w-full overflow-x-clip">
      <div className={cn(CONTAINER, SCREEN_TOP, 'relative z-10')}>
        <h1 className="sr-only">Wynajem sprzętu</h1>

        <RentalSearch query={listing.query} onQuery={listing.setQuery} />

        <RentalToolbar
          categories={categories}
          counts={listing.counts}
          active={listing.active}
          onToggle={listing.toggle}
          filtered={listing.filtered}
          onReset={listing.resetFilters}
          total={listing.visible.length}
          sort={listing.sort}
          onSort={listing.setSort}
        />

        {listing.visible.length === 0 ? (
          <RentalEmptyState
            query={listing.settledQuery}
            onReset={listing.resetFilters}
            locale={locale}
          />
        ) : (
          <ul className="mt-5 grid list-none grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {listing.visible.map((item) => (
              <li key={item.slug} className="h-full">
                <RentalCard
                  item={item}
                  href={`/${locale}/wynajem/${item.slug}`}
                  quote={listing.quoteFor(item)}
                />
              </li>
            ))}
          </ul>
        )}

        <RentalListingTerms
          locale={locale}
          fromDailyNet={fromDailyNet}
          className="mt-16 sm:mt-20"
        />
      </div>
    </div>
  )
}
